import qs from "querystring";
import { useEffect, useState } from "react";
import useSWR from "swr";

import { axios } from "@/utils/axios";
import logger from "@/utils/logger";
import { getNormelisedFetchOptions } from "@/utils/requests";

import {
    LocationProps,
    APILocationProps,
    LatLngLiteralProps,
} from "../interfaces/locations/location-interface";
import {
    transformCategoryStoreLocations,
    transformStoreLocations,
} from "../interfaces/locations/location-transform";
import { APIStoreProps, StoreProps } from "../interfaces/stores-interface";
import { SHOP_DIRECTORY_API_HOST } from "../shared/constants";
import { Locale } from "../types/locale";

type SWRLocationProps = {
    data: {
        data: APILocationProps[];
    };
    included: APIStoreProps[];
};

async function fetcher(url: string, options?: Record<string, unknown>): Promise<SWRLocationProps> {
    const res = await axios(url, options);
    return {
        data: res.data.data,
        included: res.data.included,
    };
}

export function useCategoryLocations(
    slug: string,
    locale: Locale,
    location: LatLngLiteralProps,
    radius: number
): Array<LocationProps> {
    const [locations, setLocations] = useState<Array<LocationProps>>([]);
    const { lat, lng } = location;
    const queryString = qs.stringify({
        locale: locale,
        hasInStore: true,
        offset: 0,
        perPage: 250,
        "by_user_location[latitude]": lat,
        "by_user_location[longitude]": lng,
        "by_user_location[search_radius]": radius,
    });
    const url = `${SHOP_DIRECTORY_API_HOST}/api/v1/categories/${slug}/locations?${queryString}`;
    const { data, error } = useSWR<SWRLocationProps>(url, fetcher);
    useEffect(() => {
        try {
            if (!Array.isArray(data?.data)) {
                return;
            }

            setLocations(transformCategoryStoreLocations(data?.data, data?.included));
        } catch (ex) {
            logger.logException(ex, `Unable to transform locations`);
            setLocations([]);
        }
    }, [data]);

    logger.logException(error, `Unable to fetch locations`);

    return locations;
}

export function useStoreLocations(
    locale: Locale,
    location: LatLngLiteralProps,
    store: StoreProps,
    radius: number
): Array<LocationProps> {
    const [locations, setLocations] = useState<Array<LocationProps>>([]);
    const { lat, lng } = location;
    const queryString = qs.stringify({
        locale: locale,
        has_in_store: true,
        has_offer: false,
        offset: 0,
        perPage: 250,
        "by_user_location[latitude]": lat,
        "by_user_location[longitude]": lng,
        "by_user_location[search_radius]": radius,
    });
    const url = `${SHOP_DIRECTORY_API_HOST}/api/v1/stores/${store.id}/locations?${queryString}`;
    const { data, error } = useSWR<SWRLocationProps>(url, fetcher);
    useEffect(() => {
        try {
            if (!Array.isArray(data?.data)) {
                return;
            }

            setLocations(transformStoreLocations(data?.data, data.included?.[0]));
        } catch (ex) {
            logger.logException(ex, `Unable to transform locations`);
            setLocations([]);
        }
    }, [data]);

    logger.logException(error, `Unable to fetch locations`);

    return locations;
}
